// ...existing code...
import { useEffect, useState, useRef } from "react";
import { hasActiveCSSTransition, isElementActuallyVisible, observeNodeMutation, parseMaxTime } from "../utils/dom";

export function useVisible({ ref, onVisible, checkParents = false, observeResize = false }) {
	const [el, setEl] = useState(ref);
	const calledRef = useRef(false);
	const mutationObserversRef = useRef([]);
	const resizeObserverRef = useRef(null);
	const intersectionObserverRef = useRef(null);
	const transitionTimeoutRef = useRef(null);

	useEffect(() => {
		updateElement(ref)
	}, [ref])

	function cleanupObservers(cleanCalled = false) {
		mutationObserversRef.current.forEach(obs => obs.disconnect());
		mutationObserversRef.current = [];
		if (resizeObserverRef.current) {
			resizeObserverRef.current.disconnect();
			resizeObserverRef.current = null;
		}
		if (intersectionObserverRef.current) {
			intersectionObserverRef.current.disconnect();
			intersectionObserverRef.current = null;
		}
		if (transitionTimeoutRef.current) {
			clearTimeout(transitionTimeoutRef.current);
			transitionTimeoutRef.current = null;
		}
		if (cleanCalled) calledRef.current = false;
	}

	async function scheduleCheckVisibility(el) {
		// small throttle using rAF
		if (calledRef._raf) return;
		calledRef._raf = requestAnimationFrame(() => {
			calledRef._raf = null;
			if (el && hasActiveCSSTransition(el, checkParents)) {
				waitForTransitionsToEnd(el, checkParents).then(() => {
					const visible = isElementActuallyVisible(el, checkParents);
					if (visible && !calledRef.current) {
						calledRef.current = true;
						onVisible?.();
					} else if (!visible) {
						calledRef.current = false;
					}
				});
				return;
			}

			const visible = isElementActuallyVisible(el, checkParents);
			if (visible && !calledRef.current) {
				calledRef.current = true;
				onVisible?.();
			} else if (!visible) {
				calledRef.current = false;
			}
		});
	}

	function waitForTransitionsToEnd(node, checkParents) {
		return new Promise((resolve) => {
			if (transitionTimeoutRef.current) {
				clearTimeout(transitionTimeoutRef.current);
				transitionTimeoutRef.current = null;
			}

			let maxDuration = 0;
			let n = node;
			const nodesToWatch = [];
			while (n && n.nodeType === 1) {
				const style = window.getComputedStyle(n);
				const tDur = parseMaxTime(style.transitionDuration);
				const aDur = parseMaxTime(style.animationDuration);
				if ((tDur > 0) || (aDur > 0)) {
					nodesToWatch.push(n);
					maxDuration = Math.max(maxDuration, tDur, aDur);
				}
				if (!checkParents) break;
				n = n.parentElement;
			}

			if (nodesToWatch.length === 0) {
				resolve();
				return;
			}

			let settled = false;
			const finish = () => {
				if (settled) return;
				settled = true;
				if (transitionTimeoutRef.current) {
					clearTimeout(transitionTimeoutRef.current);
					transitionTimeoutRef.current = null;
				}
				resolve();
			};

			const fallback = Math.max(50, Math.ceil(maxDuration) + 100);
			transitionTimeoutRef.current = setTimeout(finish, fallback);
		});
	}

	useEffect(() => {
		if (!el) return;
		cleanupObservers();

		const THRESHOLD = 0.01;
		intersectionObserverRef.current = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting && entry.intersectionRatio > THRESHOLD) {
						scheduleCheckVisibility?.(el);
					}
				});
			},
			{ threshold: [THRESHOLD] },
		);
		intersectionObserverRef.current.observe(el);

		let node = el;
		if (checkParents) {
			while (node && node.nodeType === 1) {
				mutationObserversRef.current.push(observeNodeMutation(node, () => scheduleCheckVisibility(el)));
				if (!checkParents) break;
				node = node.parentElement;
			}
		}

		if (observeResize && window.ResizeObserver) {
			resizeObserverRef.current = new ResizeObserver(() => scheduleCheckVisibility(el));
			resizeObserverRef.current.observe(el);
		}

		scheduleCheckVisibility(el);

		return () => {
			cleanupObservers();
		};
	}, [el, onVisible, checkParents]);

	function updateElement(ref) {
		setEl(ref?.current ? ref.current : ref);
	}

	return { updateElement }
}